import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CalendarEvent, CalendarEventParticipant } from '../../services/definitions.service';
import { DataModelService } from '../../services/data-model.service';
import { NotiBottomSheetComponent, NotiSheetData, NotiType } from '../../noti-bottom-sheet/noti-bottom-sheet.component';
import {
  CalendarEventAddParticipantDialogComponent,
  AddParticipantDialogResult,
} from './calendar-event-add-participant-dialog.component';
import firebase from 'firebase/app';
import 'firebase/firestore';

export interface ParticipantsDialogData {
  event: CalendarEvent;
}

@Component({
  selector: 'app-calendar-event-participants-dialog',
  templateUrl: './calendar-event-participants-dialog.component.html',
  styleUrls: ['./calendar-event-participants-dialog.component.css'],
})
export class CalendarEventParticipantsDialogComponent implements OnInit {
  participants: CalendarEventParticipant[] = [];
  isLoading: boolean = true;
  processingIds: Set<string> = new Set();

  constructor(
    public dialogRef: MatDialogRef<CalendarEventParticipantsDialogComponent>,
    private dialog: MatDialog,
    private bottomSheet: MatBottomSheet,
    private dataModel: DataModelService,
    @Inject(MAT_DIALOG_DATA) public data: ParticipantsDialogData
  ) {}

  ngOnInit(): void {
    this.loadParticipants();
  }

  /**
   * Loads all participants from the event's participant subcollection.
   */
  async loadParticipants(): Promise<void> {
    this.isLoading = true;

    if (!this.data.event?.path) {
      this.isLoading = false;
      return;
    }

    const result = await this.dataModel.getAllCalendarEventParticipants(
      this.data.event.path
    );

    if (result.result && Array.isArray(result.data)) {
      this.participants = result.data.map((raw) =>
        this.dataModel.deserializeCalendarEventParticipant(raw)
      );
    } else {
      this.participants = [];
    }

    this.isLoading = false;
  }

  /**
   * Returns a display name for a participant.
   */
  getParticipantName(participant: CalendarEventParticipant): string {
    const first = participant.firstName || participant.prticipantFirstName || '';
    const last = participant.lastName || participant.participantLastName || '';
    return `${first} ${last}`.trim() || 'Unbekannt';
  }

  /**
   * Toggles the attending state of a participant and persists it.
   */
  async onAttendingChange(participant: CalendarEventParticipant): Promise<void> {
    if (!participant.path || this.isProcessing(participant)) {
      return;
    }

    this.processingIds.add(participant.id);
    const newValue = !participant.attending;

    const result = await this.dataModel.updateCalendarEventParticipant(
      participant.path,
      { attending: newValue }
    );

    if (result.result) {
      participant.attending = newValue;
    } else {
      this.bottomSheet.open(NotiBottomSheetComponent, {
        data: new NotiSheetData(
          'Status konnte nicht gespeichert werden.',
          NotiType.Error,
          3000,
          false,
          false
        ),
        panelClass: 'noti-bottom-sheet-info',
        disableClose: false,
      });
    }

    this.processingIds.delete(participant.id);
  }

  /**
   * Removes a participant from the event after confirmation.
   */
  async onDeleteParticipant(participant: CalendarEventParticipant): Promise<void> {
    if (!participant.path || this.isProcessing(participant)) {
      return;
    }

    const sheetRef = this.bottomSheet.open(NotiBottomSheetComponent, {
      data: new NotiSheetData(
        `"${this.getParticipantName(participant)}" wirklich entfernen?`,
        NotiType.Warn,
        0,
        true,
        true,
        'Entfernen',
        'Abbrechen'
      ),
      panelClass: 'noti-bottom-sheet-info',
      disableClose: false,
    });

    sheetRef.afterDismissed().subscribe(async (confirmed) => {
      if (!confirmed) {
        return;
      }

      this.processingIds.add(participant.id);

      const result = await this.dataModel.deleteCalendarEventParticipant(
        participant.path
      );

      if (result.result) {
        this.participants = this.participants.filter(
          (p) => p.path !== participant.path
        );
      } else {
        this.bottomSheet.open(NotiBottomSheetComponent, {
          data: new NotiSheetData(
            'Teilnehmer konnte nicht entfernt werden.',
            NotiType.Error,
            3000,
            false,
            false
          ),
          panelClass: 'noti-bottom-sheet-info',
          disableClose: false,
        });
      }

      this.processingIds.delete(participant.id);
    });
  }

  /**
   * Opens the add-participant dialog and adds the selected person.
   */
  onAddParticipant(): void {
    const addDialogRef = this.dialog.open(CalendarEventAddParticipantDialogComponent, {
      width: '400px',
      maxWidth: '95vw',
      data: {
        eventPath: this.data.event?.path,
        existingUserIds: this.participants.map((p) => p.userId),
      },
    });

    addDialogRef.afterClosed().subscribe(async (result: AddParticipantDialogResult) => {
      if (!result?.confirmed || !result.user || !this.data.event?.path) {
        return;
      }

      const participantData: CalendarEventParticipant = {
        firstName: result.user.personalData?.firstName || '',
        lastName: result.user.personalData?.lastName || '',
        prticipantFirstName: result.user.personalData?.firstName || '',
        participantLastName: result.user.personalData?.lastName || '',
        userId: result.user.uid || '',
        attending: true,
        created: firebase.firestore.Timestamp.fromDate(new Date()),
      };

      const addResult = await this.dataModel.addCalendarEventParticipant(
        this.data.event.path,
        participantData
      );

      if (addResult.result) {
        // Reload list to get the new participant with its path
        await this.loadParticipants();
        this.bottomSheet.open(NotiBottomSheetComponent, {
          data: new NotiSheetData(
            'Teilnehmer wurde hinzugefügt.',
            NotiType.Success,
            2500,
            false,
            false
          ),
          panelClass: 'noti-bottom-sheet-info',
          disableClose: false,
        });
      } else {
        this.bottomSheet.open(NotiBottomSheetComponent, {
          data: new NotiSheetData(
            'Teilnehmer konnte nicht hinzugefügt werden.',
            NotiType.Error,
            3000,
            false,
            false
          ),
          panelClass: 'noti-bottom-sheet-info',
          disableClose: false,
        });
      }
    });
  }

  /**
   * Returns whether a participant is currently being processed.
   */
  isProcessing(participant: CalendarEventParticipant): boolean {
    return this.processingIds.has(participant.id);
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
